import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react';
import { Spin } from 'antd';
import CaptionInput from './CaptionInput';
import type { ImageAlign } from './image-node';
import ImageToolbar from './ImageToolbar';
import { useImagePicker } from './image-picker-context';

const ALIGN_CLASS: Record<ImageAlign, string> = {
  left: 'mr-auto ml-0 max-w-[55%]',
  center: 'mx-auto',
  right: 'ml-auto mr-0 max-w-[55%]',
  full: 'mx-auto w-full',
};

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** 換圖動作由 Tiptap.tsx 經 context 提供（NodeView 收不到 props）；唯讀預覽沒有 → 不顯示。 */
function useReplaceActions(getPos: () => number | undefined) {
  const picker = useImagePicker();
  if (!picker) return {};
  return {
    onReplaceFromLibrary: () => picker.openLibrary({ mode: 'replace', getPos }),
    onReplaceUpload: (file: File) => picker.uploadReplace(getPos, file),
  };
}

/** WordPress 風格圖片區塊：選取時顯示對齊／換圖工具列＋圖說輸入。 */
export default function ImageNodeView({ node, updateAttributes, selected, deleteNode, editor, getPos }: ReactNodeViewProps) {
  const replace = useReplaceActions(getPos);
  const a = node.attrs;
  const align = (str(a.align) || 'center') as ImageAlign;
  const caption = str(a.caption);
  const uploading = Boolean(a.uploading);
  const editable = editor.isEditable;
  const sideAligned = align === 'left' || align === 'right';
  const ring = selected ? 'ring-2 ring-[#dd7900]' : '';

  return (
    <NodeViewWrapper className="my-3" data-align={align}>
      <figure className={`relative flex flex-col ${sideAligned ? '' : 'items-center'}`}>
        {editable && selected && !uploading && (
          <ImageToolbar
            align={align}
            onAlign={(v) => updateAttributes({ align: v })}
            onDelete={deleteNode}
            {...replace}
          />
        )}

        <div className={`relative overflow-hidden rounded-md ${ALIGN_CLASS[align]} ${ring}`}>
          {uploading ? (
            <div className="flex h-40 w-64 items-center justify-center bg-gray-100 text-gray-400">
              <Spin /> <span className="ml-2 text-xs">上傳中…</span>
            </div>
          ) : (
            <img src={str(a.src)} alt={caption || str(a.alt)} className="block h-auto w-full" />
          )}
        </div>

        {!uploading && (
          <Caption editable={editable} value={caption} onChange={(v) => updateAttributes({ caption: v })} />
        )}
      </figure>
    </NodeViewWrapper>
  );
}

/** 圖說：編輯模式可輸入；唯讀模式僅在有內容時渲染 figcaption。 */
function Caption({ editable, value, onChange }: { editable: boolean; value: string; onChange: (v: string) => void }) {
  if (editable) return <CaptionInput value={value} onCommit={onChange} />;
  if (!value) return null;
  return <figcaption className="mt-1 text-center text-sm text-gray-500">{value}</figcaption>;
}
